import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  detailDocument,
  documentList,
  updateDocumentStatus,
} from "./callbacks";
import { IDocumentPayloadType } from "../../../types/api/document-types";


export const useDocumentList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<
      TResponseWithQData<IDocumentPayloadType[]>,
      Error
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<
  TResponseWithQData<IDocumentPayloadType[]>,
  Error
> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<
    TResponseWithQData<IDocumentPayloadType[]>,
    Error
  >({
    queryKey: [
      API_ENDPOINTS.DOCUMENT_LIST,
      { ...queryParams, total: null },
    ],
    queryFn: async () => {
      const res = await documentList(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: true,
    
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({  ...params }));
    },
  };
};

export const useDocumentDetail = (
  id: string,
  options?: Omit<
    UseQueryOptions<TResponse<IDocumentPayloadType>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IDocumentPayloadType>, Error> => {
  return useQuery<TResponse<IDocumentPayloadType>, Error>({
    queryKey: [API_ENDPOINTS.DOCUMENT_LIST, id],
    queryFn: async () => {
      const res = await detailDocument(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};



export const useDocumentStatusServiceUpdate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDocumentPayloadType>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IDocumentPayloadType>,
    unknown,
    IDocumentPayloadType
  > = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await updateDocumentStatus(data);
      return response.data;
    },

    onSuccess: (res, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.DOCUMENT_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return old;

          const exists = old.data.some((v: any) => v._id === res.data._id);

          return {
            ...old,
            data: exists
              ? old.data.map((v: any) =>
                  v._id === res.data._id ? res.data : v
                )
              : [res.data, ...old.data],
          };
        }
      );

      // queryClient.invalidateQueries({
      //   queryKey: [API_ENDPOINTS.DOCUMENT_LIST, variables?._id],
      // });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};